import { logError } from '@ptek/client-logger';
// Transition constants
const TRANSITION_DURATION = '0.3s';
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
/**
 * Manages fullscreen functionality for widgets
 */
export class FullscreenManager {
    constructor(onStateChange, onProgrammaticResize, sizeConstraints) {
        Object.defineProperty(this, "isFullscreenState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "preFullscreenPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "preFullscreenSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "container", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onStateChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onProgrammaticResize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "originalBorderRadius", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "originalBorder", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "originalTransition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "manualResizeDetected", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "sizeConstraints", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.onStateChange = onStateChange;
        this.onProgrammaticResize = onProgrammaticResize;
        this.sizeConstraints = sizeConstraints;
        this.handleEscapeKey = this.handleEscapeKey.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
    }
    /**
     * Sets the container element that will be managed for fullscreen
     */
    setContainer(container) {
        this.container = container;
        // Store original styling values when container is set
        if (container) {
            this.originalBorderRadius = container.style.borderRadius || '8px'; // Default theme value
            this.originalBorder = container.style.border || '1px solid #ccc'; // Default theme value
            this.originalTransition = container.style.transition || '';
        }
    }
    /**
     * Notifies the fullscreen manager that manual resize has occurred
     * This will transition out of fullscreen mode smoothly to prevent glitchy behavior
     */
    notifyManualResize() {
        if (this.isFullscreenState && !this.manualResizeDetected) {
            this.manualResizeDetected = true;
            // Immediately remove fullscreen constraints to allow smooth resizing
            this.transitionOutOfFullscreenForResize();
            // Exit fullscreen state since user is manually resizing
            // This makes the next toggle go back to fullscreen from the current position
            this.isFullscreenState = false;
            this.onStateChange?.(false);
        }
    }
    /**
     * Enters fullscreen mode
     * @param skipTransition - If true, applies fullscreen styles immediately without transition
     */
    async enterFullscreen(skipTransition = false) {
        if (!this.container || this.isFullscreenState) {
            return false;
        }
        try {
            // Reset manual resize flag
            this.manualResizeDetected = false;
            // Always store current position and size as the new pre-fullscreen state
            // This ensures that after resize-out-of-fullscreen, the next toggle uses the current position
            this.preFullscreenPosition = {
                x: parseInt(this.container.style.left) || 0,
                y: parseInt(this.container.style.top) || 0,
            };
            this.preFullscreenSize = {
                width: this.container.offsetWidth,
                height: this.container.offsetHeight,
            };
            // Apply fullscreen styles
            this.applyFullscreenStyles(skipTransition);
            // Add escape key listener
            document.addEventListener('keydown', this.handleEscapeKey);
            // Add window resize listener to keep widget fullscreen when window is resized
            window.addEventListener('resize', this.handleWindowResize);
            this.isFullscreenState = true;
            this.onStateChange?.(true);
            return true;
        }
        catch (error) {
            logError('FullscreenManager', 'Failed to enter fullscreen:', error);
            return false;
        }
    }
    /**
     * Exits fullscreen mode
     * @param skipTransition - If true, restores styles immediately without transition
     */
    async exitFullscreen(skipTransition = false) {
        if (!this.container || !this.isFullscreenState) {
            return false;
        }
        try {
            // Remove window resize listener
            window.removeEventListener('resize', this.handleWindowResize);
            // Only restore previous position/size if we have a return point and no manual resize occurred
            if (this.preFullscreenPosition &&
                this.preFullscreenSize &&
                !this.manualResizeDetected) {
                this.restoreNormalStyles(skipTransition);
            }
            else {
                // No return point or manual resize occurred - just remove fullscreen styling but keep current size/position
                this.removeFullscreenStyling(skipTransition);
            }
            document.removeEventListener('keydown', this.handleEscapeKey);
            this.isFullscreenState = false;
            this.manualResizeDetected = false;
            this.onStateChange?.(false);
            return true;
        }
        catch (error) {
            logError('FullscreenManager', 'Failed to exit fullscreen:', error);
            return false;
        }
    }
    /**
     * Toggles fullscreen mode
     */
    async toggleFullscreen() {
        if (this.isFullscreenState) {
            return this.exitFullscreen();
        }
        else {
            return this.enterFullscreen();
        }
    }
    /**
     * Returns current fullscreen state
     */
    isFullscreen() {
        return this.isFullscreenState;
    }
    /**
     * Gets the actual viewport dimensions accounting for scroll bars
     */
    getViewportDimensions() {
        return {
            width: document.documentElement.clientWidth,
            height: document.documentElement.clientHeight,
        };
    }
    /**
     * Applies fullscreen styles to the container
     */
    applyFullscreenStyles(skipTransition = false) {
        if (!this.container)
            return;
        if (!skipTransition) {
            this.container.style.transition = `all ${TRANSITION_DURATION} ${TRANSITION_EASING}`;
        }
        const viewport = this.getViewportDimensions();
        let { width, height } = viewport;
        // Apply max size constraints if they exist
        if (this.sizeConstraints?.maxWidth !== undefined) {
            width = Math.min(width, this.sizeConstraints.maxWidth);
        }
        if (this.sizeConstraints?.maxHeight !== undefined) {
            height = Math.min(height, this.sizeConstraints.maxHeight);
        }
        // Center the widget if it's smaller than viewport due to max constraints
        const left = width < viewport.width ? (viewport.width - width) / 2 : 0;
        const top = height < viewport.height ? (viewport.height - height) / 2 : 0;
        this.container.style.position = 'fixed';
        this.container.style.top = `${top}px`;
        this.container.style.left = `${left}px`;
        this.container.style.width = `${width}px`;
        this.container.style.height = `${height}px`;
        this.container.style.zIndex = '1000000';
        this.container.style.borderRadius = '0';
        this.container.style.border = 'none';
        this.container.classList.add('widget-fullscreen');
    }
    /**
     * Transitions out of fullscreen mode when manual resize is detected
     * This removes fullscreen constraints while keeping the fullscreen state active
     */
    transitionOutOfFullscreenForResize() {
        if (!this.container)
            return;
        this.container.style.transition = 'none';
        this.container.style.width = 'auto';
        this.container.style.height = 'auto';
        this.container.style.position = 'fixed';
        this.container.style.top = '0';
        this.container.style.left = '0';
        this.container.style.zIndex = '1000000';
        this.container.style.borderRadius = '0';
        this.container.style.border = 'none';
        this.container.classList.add('widget-fullscreen');
    }
    /**
     * Restores normal (non-fullscreen) styles
     */
    restoreNormalStyles(skipTransition = false) {
        if (!this.container ||
            !this.preFullscreenPosition ||
            !this.preFullscreenSize)
            return;
        if (!skipTransition) {
            this.container.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        }
        this.container.style.position = 'fixed';
        this.container.style.top = `${this.preFullscreenPosition.y}px`;
        this.container.style.left = `${this.preFullscreenPosition.x}px`;
        this.container.style.width = `${this.preFullscreenSize.width}px`;
        this.container.style.height = `${this.preFullscreenSize.height}px`;
        this.container.style.zIndex = '999999';
        this.container.style.borderRadius = this.originalBorderRadius;
        this.container.style.border = this.originalBorder;
        this.container.classList.remove('widget-fullscreen');
        if (!skipTransition) {
            setTimeout(() => {
                if (this.container) {
                    this.container.style.transition = this.originalTransition;
                }
            }, 300);
        }
    }
    /**
     * Removes fullscreen-specific styling without restoring previous size/position
     * Used when user has manually resized and we want to keep current dimensions
     */
    removeFullscreenStyling(skipTransition = false) {
        if (!this.container)
            return;
        if (!skipTransition) {
            this.container.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        }
        this.container.style.zIndex = '999999';
        this.container.style.borderRadius = this.originalBorderRadius;
        this.container.style.border = this.originalBorder;
        this.container.classList.remove('widget-fullscreen');
        if (!skipTransition) {
            setTimeout(() => {
                if (this.container) {
                    this.container.style.transition = this.originalTransition;
                }
            }, 300);
        }
    }
    /**
     * Handles escape key press to exit fullscreen
     */
    handleEscapeKey(event) {
        if (event.key === 'Escape' && this.isFullscreenState) {
            event.preventDefault();
            this.exitFullscreen();
        }
    }
    /**
     * Updates widget dimensions programmatically without triggering manual resize detection
     * Used by window resize handler to keep widget fullscreen
     */
    updateDimensionsProgrammatically(width, height) {
        if (!this.container || !this.onProgrammaticResize)
            return;
        this.onProgrammaticResize({ x: 0, y: 0 }, { width, height });
    }
    /**
     * Handles window resize events when in fullscreen mode
     * Updates the widget dimensions to match the new window size
     */
    handleWindowResize() {
        if (!this.isFullscreenState ||
            !this.container ||
            this.manualResizeDetected) {
            return;
        }
        const viewport = this.getViewportDimensions();
        let { width, height } = viewport;
        // Apply max size constraints if they exist
        if (this.sizeConstraints?.maxWidth !== undefined) {
            width = Math.min(width, this.sizeConstraints.maxWidth);
        }
        if (this.sizeConstraints?.maxHeight !== undefined) {
            height = Math.min(height, this.sizeConstraints.maxHeight);
        }
        // Center the widget if it's smaller than viewport due to max constraints
        const left = width < viewport.width ? (viewport.width - width) / 2 : 0;
        const top = height < viewport.height ? (viewport.height - height) / 2 : 0;
        requestAnimationFrame(() => {
            if (this.container &&
                this.isFullscreenState &&
                !this.manualResizeDetected) {
                this.updateDimensionsProgrammatically(width, height);
                // Also update position if widget is centered due to max constraints
                if (left > 0 || top > 0) {
                    this.container.style.left = `${left}px`;
                    this.container.style.top = `${top}px`;
                }
            }
        });
    }
    /**
     * Cleanup method to remove event listeners
     */
    destroy() {
        document.removeEventListener('keydown', this.handleEscapeKey);
        window.removeEventListener('resize', this.handleWindowResize);
    }
}
//# sourceMappingURL=FullscreenManager.js.map